import React, { useEffect, useMemo, useRef } from "react";
import { Button, Form, Modal, Select } from "antd";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const DEFAULT_AUDIENCE_PROFILE = "other";
const EDITABLE_ROLES = ["SUPER_ADMIN", "ADMIN_CLIENT"];
const INTERNAL_PLAN_CODES = ["super_admin", "platform_role"];
const ROLE_FALLBACK_LABELS = {
    SUPER_ADMIN: "Administrateur plateforme",
    ADMIN_CLIENT: "Administrateur du workspace",
    USER: "Utilisateur du workspace",
};

export const AUDIENCE_PROFILE_VALUES = [
    "legal_professional",
    "entrepreneur_executive",
    "sme_operations",
    "other",
];

export const normalizeAudienceProfile = (value) => (
    AUDIENCE_PROFILE_VALUES.includes(value) ? value : DEFAULT_AUDIENCE_PROFILE
);

export const canEditOwnAudienceProfile = (role) => EDITABLE_ROLES.includes(role);

const humanizeTechnicalLabel = (value) => (
    String(value || "")
        .trim()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .toLowerCase()
        .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase())
);

export const formatUserRoleLabel = (role, tUsers) => {
    const normalized = String(role || "").trim().toUpperCase();
    if (!normalized) {
        return tUsers("profileSettings.roleUnknown");
    }

    return tUsers(`profileSettings.roles.${normalized}`, {
        defaultValue: ROLE_FALLBACK_LABELS[normalized] || humanizeTechnicalLabel(normalized),
    });
};

const normalizePlanLabel = (plan, tUsers) => {
    const raw = plan?.name || plan?.code;
    if (!raw) return null;
    const source = String(plan?.source || "").trim().toLowerCase();
    const code = String(plan?.code || "").trim().toLowerCase();
    const normalizedRaw = String(raw).trim();
    const rawCode = normalizedRaw.toLowerCase();

    if (
        INTERNAL_PLAN_CODES.includes(source) ||
        INTERNAL_PLAN_CODES.includes(code) ||
        INTERNAL_PLAN_CODES.includes(rawCode)
    ) {
        return tUsers("profileSettings.platformAccess");
    }

    if (/[_-]/.test(normalizedRaw)) {
        return humanizeTechnicalLabel(normalizedRaw);
    }

    return String(raw).trim().toUpperCase();
};

const getCompleteAnalysisUsage = (usageByType = {}) => (
    usageByType?.complete_analysis || null
);

const formatAnalysisUsageLine = (usage, tUsers) => {
    if (!usage) {
        return tUsers("profileSettings.planUsageUnavailable");
    }
    if (usage.unlimited === true) {
        return tUsers("profileSettings.planUsageUnlimited");
    }

    const hasRemaining = usage.remaining !== null && usage.remaining !== undefined;
    const hasLimit = usage.limit !== null && usage.limit !== undefined;
    const used = Number.isFinite(Number(usage.used)) ? Number(usage.used) : 0;
    const reserved = Number.isFinite(Number(usage.reserved)) ? Number(usage.reserved) : 0;
    const remaining = hasRemaining && Number.isFinite(Number(usage.remaining))
        ? Number(usage.remaining)
        : null;
    const limit = hasLimit && Number.isFinite(Number(usage.limit))
        ? Number(usage.limit)
        : null;

    if (remaining !== null && limit !== null) {
        return tUsers("profileSettings.planUsageWithLimit", {
            used,
            reserved,
            remaining,
            limit,
        });
    }
    if (remaining !== null) {
        return tUsers("profileSettings.planUsageRemaining", { remaining });
    }
    return tUsers("profileSettings.planUsageUnavailable");
};

const ProfileSettingsModal = ({
    open,
    currentUser,
    onCancel,
    onSubmit,
    saving = false,
    plan = null,
    usageByType = {},
    isUsageLoading = false,
    onOpenUsage = null,
}) => {
    const { t: tUsers } = useTranslation("users");
    const [form] = Form.useForm();
    const canEditAudienceProfile = canEditOwnAudienceProfile(currentUser?.role);

    const audienceProfileOptions = useMemo(() => (
        AUDIENCE_PROFILE_VALUES.map((value) => ({
            value,
            label: tUsers(`audienceProfiles.${value}`),
        }))
    ), [tUsers]);

    const audienceProfile = normalizeAudienceProfile(currentUser?.audience_profile);
    const audienceProfileLabel = tUsers(`audienceProfiles.${audienceProfile}`);
    const fullName = `${currentUser?.name || ""} ${currentUser?.surname || ""}`.trim();
    const email = currentUser?.email || "";
    const roleLabel = formatUserRoleLabel(currentUser?.role, tUsers);
    const planLabel = normalizePlanLabel(plan, tUsers) || tUsers("profileSettings.planUnknown");
    const completeUsage = getCompleteAnalysisUsage(usageByType);

    // Garder la dernière valeur connue pendant le loading pour éviter le shift
    // vertical de la carte "Plan" quand on clique sur "Voir les quotas".
    const lastUsageLineRef = useRef(null);
    const computedUsageLine = formatAnalysisUsageLine(completeUsage, tUsers);
    const usageLine = isUsageLoading
        ? (lastUsageLineRef.current || tUsers("profileSettings.planUsageLoading"))
        : computedUsageLine;
    useEffect(() => {
        if (!isUsageLoading) {
            lastUsageLineRef.current = computedUsageLine;
        }
    }, [computedUsageLine, isUsageLoading]);

    const initials = useMemo(() => {
        const source = fullName || email || "?";
        return source
            .split(/[\s@.]+/)
            .filter(Boolean)
            .map((w) => w[0]?.toUpperCase())
            .slice(0, 2)
            .join("") || "?";
    }, [fullName, email]);

    useEffect(() => {
        if (!open || !canEditAudienceProfile) return;
        form.setFieldsValue({ audience_profile: audienceProfile });
    }, [audienceProfile, canEditAudienceProfile, form, open]);

    return (
        <Modal
            className="profile-settings-modal"
            title={tUsers("profileSettings.title")}
            open={open}
            onCancel={onCancel}
            onOk={canEditAudienceProfile ? () => form.submit() : undefined}
            okText={tUsers("profileSettings.save")}
            cancelText={tUsers("profileSettings.close")}
            confirmLoading={saving}
            footer={canEditAudienceProfile ? undefined : [
                <Button key="close" type="primary" onClick={onCancel}>
                    {tUsers("profileSettings.close")}
                </Button>,
            ]}
            destroyOnHidden
            width={520}
        >
            <div className="profile-settings-modal__summary">
                <span
                    className="profile-settings-modal__avatar"
                    aria-hidden="true"
                >
                    {initials}
                </span>
                <div className="profile-settings-modal__summary-text">
                    <strong>{fullName || tUsers("profileSettings.unknownUser")}</strong>
                    {email && <span className="profile-settings-modal__email">{email}</span>}
                    <span className="profile-settings-modal__role-chip">
                        <span>{tUsers("profileSettings.roleLabel")}</span>
                        {roleLabel}
                    </span>
                </div>
            </div>

            <p className="profile-settings-modal__description">
                {canEditAudienceProfile
                    ? tUsers("profileSettings.adminDescription")
                    : tUsers("profileSettings.userDescription")}
            </p>

            <div className="profile-settings-modal__license">
                <div className="profile-settings-modal__license-head">
                    <span className="profile-settings-modal__readonly-label">
                        {tUsers("profileSettings.workspacePlanTitle")}
                    </span>
                    <span className="profile-settings-modal__plan-status">
                        <span className="profile-settings-modal__plan-status-dot" aria-hidden="true" />
                        {tUsers("profileSettings.planStatusActive", {
                            defaultValue: "Actif",
                        })}
                    </span>
                </div>
                <strong className="profile-settings-modal__plan-name">{planLabel}</strong>
                <p className="profile-settings-modal__plan-description">
                    {tUsers("profileSettings.workspacePlanDescription")}
                </p>
                <div className="profile-settings-modal__usage-row">
                    <span className="profile-settings-modal__usage-line">{usageLine}</span>
                    {onOpenUsage && (
                        <Button
                            type="link"
                            size="small"
                            className="profile-settings-modal__usage-button"
                            onClick={onOpenUsage}
                            loading={isUsageLoading}
                            icon={!isUsageLoading ? <ArrowRight size={14} strokeWidth={2.5} /> : null}
                            iconPosition="end"
                        >
                            {tUsers("profileSettings.viewUsage")}
                        </Button>
                    )}
                </div>
            </div>

            {canEditAudienceProfile ? (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onSubmit}
                    initialValues={{ audience_profile: audienceProfile }}
                >
                    <Form.Item
                        name="audience_profile"
                        label={tUsers("form.audienceProfile")}
                        extra={tUsers("form.audienceProfileHelp")}
                        rules={[{ required: true, message: tUsers("form.required") }]}
                    >
                        <Select
                            data-testid="profile-audience-select"
                            placeholder={tUsers("form.audienceProfilePlaceholder")}
                            options={audienceProfileOptions}
                        />
                    </Form.Item>
                </Form>
            ) : (
                <div className="profile-settings-modal__readonly-field">
                    <span className="profile-settings-modal__readonly-label">
                        {tUsers("form.audienceProfile")}
                    </span>
                    <strong>{audienceProfileLabel}</strong>
                    <span className="profile-settings-modal__readonly-divider" aria-hidden="true" />
                    <p className="profile-settings-modal__readonly-help">
                        {tUsers("form.audienceProfileHelp")}
                    </p>
                </div>
            )}
        </Modal>
    );
};

export default ProfileSettingsModal;
